import { Injectable, inject, signal, effect } from '@angular/core';
import { NetworkService } from './network';
import { IndexedDbService, type PendingRemito } from './indexed-db';
import { DeliveryNotesApiService, type DeliveryNotesCheckoutPayload, type DeliveryNotesCheckoutItemPayload } from './delivery-notes-api';
import type { CheckOutPayload, RemitoSyncPayload } from '@core/remito.model';
import { LogService } from './log.service';
import { BusyStateService } from '@shared/services/update/busy-state';
import { ENV_CONFIG } from '@core/config/env.token';
import { dataUrlToFile } from '@shared/utils/file.utils';
import { createRequestId } from '@core/http/http-context';

/**
 * Resultado de la sincronización de un remito
 */
export interface SyncResult {
  success: boolean;
  remitoId: string;
  error?: string;
}

interface SyncOptions {
  force?: boolean;
  requestId?: string;
}

export interface SyncMetrics {
  lastStartedAt: string | null;
  lastCompletedAt: string | null;
  lastDurationMs: number | null;
  lastRunProcessed: number;
  lastRunSucceeded: number;
  lastRunFailed: number;
}

/**
 * Servicio de cola de sincronización para remitos offline.
 *
 * Maneja automáticamente la sincronización de remitos pendientes
 * cuando se detecta conexión a internet. Implementa:
 * - Reintentos exponenciales (backoff)
 * - Límite máximo de intentos
 * - Procesamiento secuencial de la cola
 *
 * @example
 * ```typescript
 * const sync = inject(SyncQueueService);
 *
 * // La sincronización es automática cuando hay conexión
 * // Pero también se puede forzar manualmente:
 * await sync.syncAll();
 * ```
 */
@Injectable({ providedIn: 'root' })
export class SyncQueueService {
  private readonly network = inject(NetworkService);
  private readonly db = inject(IndexedDbService);
  private readonly api = inject(DeliveryNotesApiService);
  private readonly logger = inject(LogService);
  private readonly busyState = inject(BusyStateService);
  private readonly config = inject(ENV_CONFIG);
  private readonly hasIndexedDbSupport = typeof indexedDB !== 'undefined';
  private readonly syncDisabled = this.config.offlineSyncEnabled === false;

  /** Indica si hay una sincronización en progreso */
  readonly isSyncing = signal<boolean>(false);

  /** Contador de remitos pendientes */
  readonly pendingCount = signal<number>(0);

  /** Contador de remitos que agotaron los reintentos automáticos */
  readonly failedCount = signal<number>(0);

  /** Contador interno de remitos elegibles para sincronización automática */
  private readonly syncableCount = signal<number>(0);

  /** Último error de sincronización */
  readonly lastError = signal<string | null>(null);
  readonly metrics = signal<SyncMetrics>({
    lastStartedAt: null,
    lastCompletedAt: null,
    lastDurationMs: null,
    lastRunProcessed: 0,
    lastRunSucceeded: 0,
    lastRunFailed: 0
  });

  /** Máximo número de reintentos por remito */
  private readonly MAX_ATTEMPTS = 5;

  /** Delays para backoff exponencial (en ms) */
  private readonly BACKOFF_DELAYS = [1000, 2000, 5000, 10000, 30000];

  private dbInitialized = false;

  constructor() {
    this.watchNetwork();
    effect(() => {
      this.busyState.setSendingActive('sync-queue', this.isSyncing());
    });
    // Inicializamos el conteo de pendientes al cargar el servicio cuando IndexedDB está disponible
    if (this.hasIndexedDbSupport) {
      void this.ensureDbInitialized().catch(error => {
        this.logger.error('No se pudo inicializar la base offline al iniciar', { error: error instanceof Error ? error.message : String(error) });
      });
    } else {
      this.logger.warn('IndexedDB no está disponible. La cola offline permanecerá deshabilitada.');
    }
    if (this.syncDisabled) {
      this.logger.warn('Sincronización offline deshabilitada por configuración de entorno');
    }
  }

  /**
   * Inicializa la base de datos y actualiza el contador (lazy)
   */
  private async ensureDbInitialized(): Promise<void> {
    if (!this.hasIndexedDbSupport) {
      throw new Error('IndexedDB no está disponible en este dispositivo. El modo offline no se puede utilizar.');
    }

    if (this.dbInitialized) return;

    try {
      await this.db.init();
      await this.refreshCounts();
      this.dbInitialized = true;
    } catch (error) {
      this.logger.error('Error al inicializar IndexedDB en SyncQueue', { error: error instanceof Error ? error.message : String(error) });
      throw error;
    }
  }

  /**
   * Observa cambios en la conexión y sincroniza automáticamente
   */
  private watchNetwork(): void {
    effect(() => {
      if (this.syncDisabled) {
        return;
      }
      const online = this.network.isOnline();
      const readyToSync = this.syncableCount();
      if (online && readyToSync > 0 && !this.isSyncing()) {
        this.logger.info('Conexión detectada, iniciando sincronización automática');
        this.syncAll();
      }
    });
  }

  /**
   * Agrega un remito a la cola de sincronización junto con su metadata de Check-in
   */
  /**
   * Agrega un remito a la cola de sincronización (nuevo modelo)
   * @param remitoPayload - RemitoSyncPayload completo
   * @param observation - observación
   */
  async addToQueue(
    remitoPayload: RemitoSyncPayload,
    observation: string
  ): Promise<PendingRemito> {
    await this.ensureDbInitialized();
    const deliveryNotes = remitoPayload.deliveryNotes;
    if (deliveryNotes.length === 0) {
      throw new Error('No hay imágenes para encolar');
    }
    const offlineImages = await Promise.all(deliveryNotes.map(note => this.blobToDataUrl(note.image)));
    const pending = await this.db.savePending({
      qrCode: deliveryNotes[0].qrCode,
      imageKeys: deliveryNotes.map(note => note.imageKey),
      images: offlineImages,
      sessionId: remitoPayload.sessionId,
      checkIn: remitoPayload.checkIn,
      observation,
      createdAt: Date.now(),
    });
    await this.refreshCounts();
    if (!this.syncDisabled && this.network.isOnline()) {
      this.syncAll();
    }
    return pending;
  }

  /**
   * Sincroniza todos los remitos pendientes
   */
  async syncAll(options: SyncOptions = {}): Promise<SyncResult[]> {
    if (this.syncDisabled) {
      this.logger.warn('syncAll deshabilitado: sincronización temporalmente apagada');
      return [];
    }
    const { force = false } = options;
    const requestId = options.requestId ?? createRequestId('sync-all');

    if (this.isSyncing()) {
      this.logger.info('Se intentó iniciar syncAll mientras otra sincronización está en curso');
      return [];
    }

    if (!this.network.isOnline()) {
      this.logger.warn('syncAll cancelado: sin conexión');
      return [];
    }

    this.isSyncing.set(true);
    this.lastError.set(null);
    const startedAt = new Date();

    try {
      await this.ensureDbInitialized();
      const pending = await this.db.getPending(force);
      this.logger.info('Iniciando sincronización masiva', { requestId, count: pending.length, force });

      const results: SyncResult[] = [];

      this.logger.info('Procesando remitos pendientes uno por uno', { requestId });
      for (const item of pending) {
        if (!this.hasCompletedCheckout(item)) {
          this.logger.info('Remito omitido en sync automática: sesión abierta sin check-out', {
            requestId,
            id: item.id,
            qrCode: item.qrCode,
            sessionId: item.sessionId
          });
          continue;
        }
        this.logger.info('Iniciando sync individual', { requestId, id: item.id, status: item.status, attempts: item.syncAttempts });
        const result = await this.syncOne(item, { force, requestId });
        this.logger.info('Resultado sync individual', { requestId, result });
        results.push(result);

        // Si perdemos conexión, detener sincronización
        if (!this.network.isOnline()) {
          this.logger.warn('Conexión perdida durante syncAll, deteniendo proceso');
          break;
        }
      }

      await this.refreshCounts();
      this.updateMetrics(startedAt, results);
      return results;

    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Error desconocido';
      this.lastError.set(errorMsg);
      this.logger.error('Error en sincronización masiva', { requestId, error: errorMsg });
      this.updateMetrics(startedAt, []);
      throw error;
    } finally {
      this.isSyncing.set(false);
    }
  }

  /**
   * Sincroniza un remito individual
   */
  private async syncOne(pending: PendingRemito, options: SyncOptions = {}): Promise<SyncResult> {
    const { force = false } = options;
    const requestId = options.requestId ?? createRequestId('sync-one');
    this.logger.info('Sincronizando remito individual', { requestId, id: pending.id, force });
    try {
      if (pending.syncAttempts >= this.MAX_ATTEMPTS && !force) {
        await this.db.updateStatus(pending.id, 'failed', 'Máximo de reintentos alcanzado');
        return {
          success: false,
          remitoId: pending.id,
          error: 'Máximo de reintentos alcanzado'
        };
      }
      await this.db.updateStatus(pending.id, 'syncing');
      await this.db.incrementAttempts(pending.id);
      const checkOut = this.normalizeCheckOut(pending);
      if (!checkOut) {
        await this.db.updateStatus(pending.id, 'pending', 'Remito pendiente de check-out para sincronizar');
        return {
          success: false,
          remitoId: pending.id,
          error: 'Remito pendiente de check-out para sincronizar'
        };
      }

      this.assertAlignedImages(pending);
      const images = await Promise.all(
        pending.images.map(async (image, index) => ({
          file: await dataUrlToFile(image, pending.imageKeys[index]),
          imageName: pending.imageKeys[index]
        }))
      );
      const deliveryNotes: DeliveryNotesCheckoutItemPayload[] = pending.imageKeys.map((imageKey, index) => ({
        qrCode: pending.qrCode,
        scanDate: new Date(pending.createdAt).toISOString(),
        location: checkOut.location,
        observation: pending.observation,
        imageKeys: [imageKey],
        pageNumber: index + 1
      }));
      const payload: DeliveryNotesCheckoutPayload = {
        sessionId: pending.sessionId,
        checkIn: pending.checkIn,
        checkout: checkOut,
        qrCode: pending.qrCode,
        scanDate: new Date(pending.createdAt).toISOString(),
        location: checkOut.location,
        observation: pending.observation,
        imageKeys: pending.imageKeys,
        deliveryNotes
      };

      await this.api.checkout({
        payload,
        images,
        imageKeys: pending.imageKeys,
        requestId
      });
      await this.db.delete(pending.id);
      this.logger.info('Remito sincronizado exitosamente', { requestId, id: pending.id });
      return {
        success: true,
        remitoId: pending.id
      };
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Error desconocido';
      this.logger.error('Error al sincronizar remito', { requestId, id: pending.id, error: errorMsg });
      const delay = this.BACKOFF_DELAYS[Math.min(pending.syncAttempts, this.BACKOFF_DELAYS.length - 1)];
      this.logger.info('Programando reintento para remito', { requestId, id: pending.id, delay });
      await this.db.updateStatus(pending.id, 'pending', errorMsg);

      return {
        success: false,
        remitoId: pending.id,
        error: errorMsg
      };
    }
  }

  /**
   * Obtiene el número de remitos pendientes
   */
  async getPendingCount(): Promise<number> {
    return this.db.getCount();
  }

  /**
   * Obtiene todos los remitos pendientes
   */
  async getPendingRemitos(): Promise<PendingRemito[]> {
    await this.ensureDbInitialized();
    return this.db.getPending();
  }

  /**
   * Verifica si existe un remito sin sincronizar para un QR específico
   */
  async hasPendingByQrCode(qrCode: string): Promise<boolean> {
    if (!qrCode?.trim()) {
      return false;
    }

    if (!this.hasIndexedDbSupport) {
      return false;
    }

    await this.ensureDbInitialized();
    return this.db.existsByQrCode(qrCode.trim());
  }

  /**
   * Limpia remitos sincronizados exitosamente
   */
  async cleanSynced(): Promise<void> {
    await this.ensureDbInitialized();
    await this.db.cleanSynced();
    await this.refreshCounts();
  }

  /**
   * Fuerza la actualización de los contadores expuestos sin modificar la cola.
   */
  async refreshStatusSnapshot(): Promise<void> {
    if (!this.hasIndexedDbSupport) {
      this.syncableCount.set(0);
      this.failedCount.set(0);
      this.pendingCount.set(0);
      return;
    }

    try {
      await this.ensureDbInitialized();
      await this.refreshCounts();
    } catch (error) {
      this.logger.warn('No se pudo actualizar los contadores de sincronización', { error: error instanceof Error ? error.message : String(error) });
    }
  }

  /**
   * Reintenta sincronizar un remito específico
   */
  async retryOne(id: string): Promise<SyncResult> {
    await this.ensureDbInitialized();
    const pending = await this.db.getById(id);
    if (!pending) {
      throw new Error(`Remito ${id} no encontrado`);
    }

    return this.syncOne(pending, { force: true, requestId: createRequestId('sync-retry') });
  }

  /**
   * Actualiza el contador de pendientes
   */
  private async refreshCounts(): Promise<void> {
    const counts = await this.db.getStatusCounts();
    const pending = await this.db.getPending(true);
    const eligible = pending.filter(item => item.status === 'pending' && this.hasCompletedCheckout(item));
    this.syncableCount.set(eligible.length);
    this.failedCount.set(counts.failed);
    this.pendingCount.set(counts.pending + counts.syncing + counts.failed);
  }

  /**
   * Intenta sincronizar automáticamente los remitos pendientes cuando el servidor está disponible.
   *
   * Retorna true si la sincronización se inició, false en caso contrario.
   */
  async autoSyncPendingIfApiAvailable(): Promise<boolean> {
    const requestId = createRequestId('sync-auto');
    this.logger.info('autoSyncPendingIfApiAvailable -> start', {
      requestId,
      hasIndexedDbSupport: this.hasIndexedDbSupport,
      isSyncing: this.isSyncing(),
      syncableCount: this.syncableCount(),
      online: this.network.isOnline()
    });

    if (!this.hasIndexedDbSupport) {
      this.logger.warn('IndexedDB no soportado, auto-sync cancelada', { requestId });
      return false;
    }

    if (this.isSyncing()) {
      this.logger.info('Se omite auto-sync porque ya hay sincronización en curso', { requestId });
      return false;
    }

    if (this.syncableCount() === 0) {
      this.logger.info('No hay remitos elegibles para auto-sync', { requestId });
      return false;
    }

    if (!this.network.isOnline()) {
      this.logger.info('Auto-sync cancelada: navegador offline', { requestId });
      return false;
    }

    try {
      await this.ensureDbInitialized();
    } catch (error) {
      this.logger.warn('No se pudo inicializar la cola para auto-sync', { requestId, error: error instanceof Error ? error.message : String(error) });
      return false;
    }

    try {
      const apiHealthy = await this.api.verificarConexion();
      if (!apiHealthy) {
        this.logger.warn('La API respondió sin estado OK, se intentará sincronizar igualmente', { requestId });
      }
    } catch (error) {
      this.logger.warn('No se pudo verificar la API, se intentará sincronizar igualmente', { requestId, error: error instanceof Error ? error.message : String(error) });
    }

    try {
      await this.syncAll({ requestId });
      this.logger.info('Auto-sync completada', { requestId });
      return true;
    } catch (error) {
      this.logger.error('Auto-sync falló', { requestId, error: error instanceof Error ? error.message : String(error) });
      return false;
    }
  }

  private hasCompletedCheckout(pending: PendingRemito): boolean {
    return this.normalizeCheckOut(pending) !== null;
  }

  private normalizeCheckOut(pending: PendingRemito): CheckOutPayload | null {
    const source = pending.checkout;
    if (!source?.createdAt || !source.location) {
      return null;
    }

    return {
      id: source.id ?? `checkout-${pending.checkIn.id}`,
      createdAt: source.createdAt,
      location: source.location,
      deviceInfo: source.deviceInfo ?? pending.checkIn.deviceInfo
    };
  }

  private assertAlignedImages(pending: PendingRemito): void {
    if (pending.images.length === 0 || pending.imageKeys.length === 0) {
      throw new Error('No hay imágenes para sincronizar');
    }

    if (pending.images.length !== pending.imageKeys.length) {
      throw new Error('La cantidad de imágenes no coincide con la cantidad de nombres de archivo');
    }
  }

  private blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        if (typeof reader.result === 'string') {
          resolve(reader.result);
          return;
        }
        reject(new Error('No se pudo convertir la imagen a data URL'));
      });
      reader.addEventListener('error', () => reject(reader.error ?? new Error('No se pudo leer la imagen')));
      reader.readAsDataURL(blob);
    });
  }

  private updateMetrics(startedAt: Date, results: SyncResult[]): void {
    const completedAt = new Date();
    const successful = results.filter(result => result.success).length;
    const failed = results.length - successful;

    this.metrics.set({
      lastStartedAt: startedAt.toISOString(),
      lastCompletedAt: completedAt.toISOString(),
      lastDurationMs: completedAt.getTime() - startedAt.getTime(),
      lastRunProcessed: results.length,
      lastRunSucceeded: successful,
      lastRunFailed: failed
    });
  }

}
